import { minutesToMilliseconds } from 'date-fns';
import type { PropsWithChildren } from 'react';
import React, { createContext, useCallback, useState } from 'react';
import { useAuthContext } from 'utils/contexts/AuthContext';
import { AuthState } from 'utils/contexts/AuthContext/AuthContextType';
import { useInterval } from 'utils/hooks/useInterval';
import { SiteState, useSiteState } from 'utils/hooks/useSiteState';

export type Site = SiteState;

interface SiteContextType {
    site?: Site;
    refreshSite: () => void;
    updateSite: (site?: Partial<Site>) => Promise<boolean>;
}

/*
 * This site availability check is a heuristic assuming that any `site_id`
 * can be arbitrarily set via URL so to verify that given site actually
 * exists check its other properties. Purposely don't check `company_id` and
 * `company_name` because they are not critical, should not be required
 * immediately and can be lazy loaded.
 */
const isSiteAvailableGuard = (
    siteToCheck: Partial<Site>
): siteToCheck is Site => !!(siteToCheck.site_id && siteToCheck.name);

// Re not-null assertion: See null check in useSiteContext hook
export const SiteContext = createContext<SiteContextType>(null!);

export const SiteProvider = ({ children }: PropsWithChildren) => {
    const { authState } = useAuthContext();
    const fetchSiteState = useSiteState();

    const [site, setSite] = useState<Site>();
    const currentSiteId = site?.site_id;

    const refreshSite = useCallback(async () => {
        if (!currentSiteId) return;

        const siteState = await fetchSiteState(currentSiteId);
        if (siteState) setSite(siteState);

        return !!siteState;
    }, [currentSiteId, fetchSiteState]);

    const updateSite = useCallback(
        async (newSite?: Partial<Site>) => {
            const siteId = newSite?.site_id;
            if (!siteId) {
                setSite(undefined);
                return false;
            }

            if (isSiteAvailableGuard(newSite)) {
                setSite(newSite);

                // since site exists don't wait for API response
                fetchSiteState(siteId).then(setSite);
                return true;
            }

            const siteState = await fetchSiteState(siteId);
            if (siteState) setSite(siteState);

            return !!siteState;
        },
        [fetchSiteState]
    );

    const refreshOnInterval = useCallback(() => {
        const authenticated = authState === AuthState.AUTHENTICATED;
        if (authenticated) {
            refreshSite();
        }
    }, [authState, refreshSite]);

    useInterval(refreshOnInterval, minutesToMilliseconds(1));

    return (
        <SiteContext
            value={{
                site,
                updateSite,
                refreshSite,
            }}
        >
            {children}
        </SiteContext>
    );
};

export const useSiteContext = () => {
    const siteContext = React.useContext(SiteContext);

    if (!siteContext) {
        throw new Error('useSiteContext must be used within a SiteProvider');
    }

    return siteContext;
};
